import { i18n } from 'boot/i18n';
import { computed } from 'vue';

import { HOLD_TASK_FACTS, holdTaskEntryOf } from '../../domain/constants/holdTaskCatalog';
import { HOLD_SUBJECT } from '../../domain/constants/holdTaskTypes';
import { useHoldCauses } from './useHoldCauses';
import { useMissingDetails } from './useMissingDetails';
import { useUncoveredServices } from './useUncoveredServices';

/**
 * @typedef {Object} FactRow
 * @property {string} key
 * @property {string} label
 * @property {string|null} [caption]
 * @property {string|null} [trailing]
 */

/** Says what the date is: when the uncovered service was delivered. */
const serviceDates = (line) => (line.from === line.to
  ? i18n.t('billingItems.holdTask.textServiceOn', { date: line.fromLabel })
  : i18n.t('billingItems.holdTask.textServicesBetween', { from: line.fromLabel, to: line.toLabel }));

/**
 * One reader per kind of fact, each taking from the task what it reads with and wording its rows.
 * `subjects` are what it can read for, `withCaption` whether its rows carry a second line, so a
 * placeholder takes the same shape. A reader may also word the card's `description`, where its facts
 * make the type's own one vaguer than it has to be.
 */
const READERS = {
  [HOLD_TASK_FACTS.UNCOVERED_SERVICES]: {
    subjects: [HOLD_SUBJECT.CLIENT],
    withCaption: true,
    use: (subject) => {
      const { lines, reading } = useUncoveredServices(() => {
        const current = subject();

        return current && { clinicId: current.clinicId, clientId: current.subjectId, holdReason: current.holdReason };
      });

      return {
        reading,
        // A letter only clears items billed to the payer that wrote it, so the payer is what a biller
        // looks for. With several payers each row names its own.
        description: computed(() => {
          const [first] = lines.value;
          const onePayer = first?.payerName
            && lines.value.every((line) => line.fundingSourceId === first.fundingSourceId);

          return onePayer
            ? i18n.t('billingItems.holdTask.textNoPayerAuthorization', { payer: first.payerName })
            : null;
        }),
        rows: computed(() => lines.value.map((line) => ({
          key: `${line.fundingSourceId}|${line.billingCode}`,
          label: [line.payerName ?? i18n.t('billingItems.holdTask.textUnknownPayer'), line.billingCode]
            .filter(Boolean).join(' · '),
          caption: serviceDates(line),
          // The card's action says the total; a row says its own share only when there is more than one.
          trailing: lines.value.length > 1
            ? i18n.tc('billingItems.holdTask.textItems', line.count, { count: line.count })
            : null,
        }))),
      };
    },
  },
  [HOLD_TASK_FACTS.MISSING_FIELDS]: {
    subjects: [HOLD_SUBJECT.CLIENT],
    withCaption: false,
    use: (subject) => {
      const { missing, reading } = useMissingDetails(() => {
        const current = subject();

        return current && { clinicId: current.clinicId, clientId: current.subjectId, holdReason: current.holdReason };
      });

      return {
        reading,
        rows: computed(() => missing.value.map((code) => ({
          key: code,
          label: i18n.t(`billingItems.holdTask.missingDetail.${code}`),
        }))),
      };
    },
  },
  [HOLD_TASK_FACTS.CAUSES]: {
    subjects: [HOLD_SUBJECT.TREATMENT_SERVICE],
    withCaption: false,
    use: (subject) => {
      const { causes, reading } = useHoldCauses(() => {
        const current = subject();

        return current && {
          clinicId: current.clinicId, field: current.field, value: current.subjectId, holdReasons: current.holdReasons,
        };
      });

      return {
        reading,
        // In the words billing items use for each cause.
        rows: computed(() => causes.value.map((reason) => ({
          key: reason,
          label: i18n.t(`billingItems.holdReason.${reason}`),
        }))),
      };
    },
  },
};

/**
 * The facts a hold task's card lists, as rows, read for the kind its catalog entry names.
 *
 * EVERY READER IS SET UP ONCE, because the card stays mounted as the task it shows changes, and a
 * composable cannot be started later. Each reads only while the task's facts are its kind, and
 * only for a subject it can read for: anything else would read some other record's items.
 *
 * @param {() => import('../../domain/model/HoldTask.types').HoldTask} hold
 * @returns {{rows: import('vue').ComputedRef<FactRow[]>, reading: import('vue').ComputedRef<boolean>,
 *   withCaption: import('vue').ComputedRef<boolean>, description: import('vue').ComputedRef<string|null>}}
 */
// eslint-disable-next-line import/prefer-default-export
export function useHoldTaskFacts(hold) {
  const entryOf = () => holdTaskEntryOf(hold()?.holdType);
  const factsOf = () => entryOf()?.facts ?? null;

  const readers = Object.fromEntries(Object.entries(READERS).map(([kind, reader]) => [kind, reader.use(() => {
    const { kind: shownKind, ...readWith } = factsOf() ?? {};
    const current = hold();

    return shownKind === kind && reader.subjects.includes(entryOf().subject)
      ? { clinicId: current.clinicId ?? null, subjectId: current.subjectId ?? null, ...readWith }
      : null;
  })]));

  const active = computed(() => readers[factsOf()?.kind] ?? null);

  return {
    rows: computed(() => active.value?.rows.value ?? []),
    reading: computed(() => active.value?.reading.value ?? false),
    withCaption: computed(() => READERS[factsOf()?.kind]?.withCaption ?? false),
    description: computed(() => active.value?.description?.value ?? null),
  };
} // useHoldTaskFacts()
